/**
 * How the stops of a line propagate during the running shift, from the
 * machines' live status transitions: each machine's status over time, the
 * time the line stood (its output machine did not run) and the machine that
 * caused it. Pure functions, unit-tested; the calculation is described in the
 * help ("Live shift KPIs").
 *
 * Cause of a stop: a machine that stands for its own reason (fault, stop,
 * setup, standby, internal wait) causes it itself. A machine waiting for the
 * upstream machine ("starved") passes the question to the previous stage of
 * the line, a machine waiting for the downstream machine ("blocked") to the
 * next one, until a machine standing for its own reason is found. Without one
 * (supply or discharge outside the line, or machines without live data) the
 * cause is "upstream" or "downstream".
 */

import type { StatusTransition } from "../../hooks/useLiveShiftData";
import { isMachineRunning } from "../../utils/machineStatus";

export type FlowState =
  | "running"
  /** stopped for its own reason: fault, stop button, door … */
  | "own"
  /** waiting for an internal unit of the machine ("(int)") */
  | "ownWait"
  /** waiting for the upstream machine */
  | "starved"
  /** waiting for the downstream machine */
  | "blocked"
  | "setup"
  | "standby";

const STARVED = /vorlauf|upstream|amont/i;
const BLOCKED = /nachlauf|downstream|aval/i;
const INTERNAL = /\(int/i;
const SETUP = /einricht|setup|rüst|ruest|réglage/i;
const STANDBY = /standby|veille/i;

/** The flow state of a status as the edge names it (German, English or French). */
export function classifyStatus(name: string | null | undefined, code?: number | null): FlowState {
  if (isMachineRunning(name, code)) return "running";
  const n = name ?? "";
  if (STARVED.test(n)) return INTERNAL.test(n) ? "ownWait" : "starved";
  if (BLOCKED.test(n)) return INTERNAL.test(n) ? "ownWait" : "blocked";
  if (SETUP.test(n)) return "setup";
  if (STANDBY.test(n)) return "standby";
  return "own";
}

/** Not producing for a reason of its own (the machine is the cause). */
export const isOwnDown = (s: FlowState): boolean =>
  s === "own" || s === "ownWait" || s === "setup" || s === "standby";

export interface Interval {
  start: number;
  end: number;
  state: FlowState;
  /** The status as the edge names it. */
  text: string;
}

/**
 * A machine's statuses from `startMs` to `nowMs`. Before its first transition
 * after the start it was in that transition's `from` status; without one, in
 * the status of its last transition before the start (`last`, the latest
 * known transition, also counts when the history has not caught up yet).
 */
export function buildIntervals(
  transitions: StatusTransition[],
  last: StatusTransition | undefined,
  startMs: number,
  nowMs: number,
): Interval[] {
  if (nowMs <= startMs) return [];
  const seen = new Set<string>();
  const all = (last ? [...transitions, last] : transitions)
    .map((tr) => ({ tr, at: Date.parse(tr.at) }))
    .filter(({ tr, at }) => {
      const key = `${tr.at}|${tr.to}`;
      if (!Number.isFinite(at) || at > nowMs || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.at - b.at);

  const before = all.filter((x) => x.at <= startMs).pop();
  const after = all.filter((x) => x.at > startMs);
  let status: { code: number; name: string } | null = before
    ? { code: before.tr.to, name: before.tr.to_name }
    : after.length
      ? { code: after[0].tr.from, name: after[0].tr.from_name }
      : null;
  if (!status) return [];

  const out: Interval[] = [];
  const push = (start: number, end: number, s: { code: number; name: string }) => {
    if (end <= start) return;
    const state = classifyStatus(s.name, s.code);
    const prev = out[out.length - 1];
    if (prev && prev.state === state && prev.text === s.name && prev.end === start) prev.end = end;
    else out.push({ start, end, state, text: s.name || `Status ${s.code}` });
  };
  let t = startMs;
  for (const x of after) {
    push(t, x.at, status);
    t = Math.max(t, x.at);
    status = { code: x.tr.to, name: x.tr.to_name };
  }
  push(t, nowMs, status);
  return out;
}

export interface FlowStation {
  sn: string;
  /** Stage in the line (0 = first); machines of a stage work in parallel. */
  stage: number;
  isOutput: boolean;
}

/** `m:<sn>` for a machine of the line, else where the cause lies outside it. */
export type CauseKey = `m:${string}` | "upstream" | "downstream" | "unknown";

export interface CauseStat {
  key: CauseKey;
  sn?: string;
  /** Time it stopped the line (the output machine did not run because of it). */
  lineDownSeconds: number;
  /** Time other machines of the line waited because of it. */
  waitingCausedSeconds: number;
  /** Its own statuses while it stopped the line, largest first. */
  reasons: { text: string; seconds: number }[];
}

export interface LineFlow {
  startMs: number;
  nowMs: number;
  /** Time the line's output did not run. */
  lineDownSeconds: number;
  /** How often the line went down. */
  lineStops: number;
  /** By line-down time, then by the waiting caused. */
  causes: CauseStat[];
  /** The line now: down since when and why. */
  current: { down: boolean; cause?: CauseKey; since?: number; reason?: string };
}

export const machineOf = (key: CauseKey): string | undefined =>
  key.startsWith("m:") ? key.slice(2) : undefined;

/**
 * The line's down time and its causes since `startMs`. The line is down while
 * none of its output machines runs; null without a status of an output
 * machine.
 */
export function analyzeLineFlow(
  stations: FlowStation[],
  lanes: Map<string, Interval[]>,
  startMs: number,
  nowMs: number,
): LineFlow | null {
  const known = stations.filter((s) => (lanes.get(s.sn)?.length ?? 0) > 0);
  const outputs = known.filter((s) => s.isOutput);
  if (outputs.length === 0 || nowMs <= startMs) return null;

  const byStage = new Map<number, FlowStation[]>();
  for (const s of stations) byStage.set(s.stage, [...(byStage.get(s.stage) ?? []), s]);
  const stages = [...byStage.keys()].sort((a, b) => a - b);

  const cuts = new Set<number>([startMs, nowMs]);
  for (const s of known) {
    for (const iv of lanes.get(s.sn)!) if (iv.start > startMs && iv.start < nowMs) cuts.add(iv.start);
  }
  const times = [...cuts].sort((a, b) => a - b);

  // the interval of each lane at a time, with a pointer per lane (times only grow)
  const pointer = new Map<string, number>();
  const intervalAt = (sn: string, t: number): Interval | undefined => {
    const lane = lanes.get(sn);
    if (!lane?.length) return undefined;
    let p = pointer.get(sn) ?? 0;
    while (p < lane.length - 1 && lane[p].end <= t) p++;
    pointer.set(sn, p);
    const iv = lane[p];
    return iv.start <= t && t < iv.end ? iv : undefined;
  };

  const stats = new Map<CauseKey, CauseStat>();
  const reasons = new Map<CauseKey, Map<string, number>>();
  const stat = (key: CauseKey): CauseStat => {
    let s = stats.get(key);
    if (!s) {
      s = { key, sn: machineOf(key), lineDownSeconds: 0, waitingCausedSeconds: 0, reasons: [] };
      stats.set(key, s);
    }
    return s;
  };

  let lineDownSeconds = 0;
  let lineStops = 0;
  let downSince = 0;
  let wasDown = false;
  let current: LineFlow["current"] = { down: false };

  // A wait keeps the cause found while it lasts: when the neighbour runs again
  // the buffers between them still refill, and that is still its doing.
  const episode = new Map<string, { start: number; key: CauseKey; reason?: string }>();

  for (let i = 0; i < times.length - 1; i++) {
    const a = times[i];
    const seconds = (times[i + 1] - a) / 1000;
    const at = new Map<string, Interval | undefined>();
    for (const s of stations) at.set(s.sn, intervalAt(s.sn, a));

    type Cause = { key: CauseKey; reason?: string };
    const causeOf = (s: FlowStation, visited: Set<string>): Cause => {
      const iv = at.get(s.sn);
      if (!iv) return { key: "unknown" };
      if (iv.state === "running" || isOwnDown(iv.state)) return { key: `m:${s.sn}`, reason: iv.text };
      const upstream = iv.state === "starved";
      const outside: CauseKey = upstream ? "upstream" : "downstream";

      let found: Cause | null = null;
      const next = stages[stages.indexOf(s.stage) + (upstream ? -1 : 1)];
      if (next !== undefined) {
        const neighbours = byStage.get(next)!.map((n) => ({ n, iv: at.get(n.sn) }));
        const standing = neighbours.find((x) => x.iv && isOwnDown(x.iv.state));
        const passing = neighbours.find((x) => x.iv?.state === iv.state && !visited.has(x.n.sn));
        if (standing) found = { key: `m:${standing.n.sn}`, reason: standing.iv!.text };
        else if (passing) found = causeOf(passing.n, new Set(visited).add(s.sn));
      } else {
        found = { key: outside };
      }

      if (found && machineOf(found.key)) {
        episode.set(s.sn, { start: iv.start, ...found });
        return found;
      }
      const kept = episode.get(s.sn);
      if (kept && kept.start === iv.start) return { key: kept.key, reason: kept.reason };
      return found ?? { key: outside };
    };

    for (const s of known) {
      const state = at.get(s.sn)?.state;
      if (state === "starved" || state === "blocked") {
        const c = causeOf(s, new Set());
        if (c.key !== `m:${s.sn}`) stat(c.key).waitingCausedSeconds += seconds;
      }
    }

    const outStates = outputs.map((o) => ({ o, iv: at.get(o.sn) }));
    const down = outStates.every((x) => x.iv && x.iv.state !== "running");
    const last = i === times.length - 2;
    if (down) {
      if (!wasDown) {
        lineStops++;
        downSince = a;
      }
      lineDownSeconds += seconds;
      const c = causeOf(outStates[0].o, new Set());
      stat(c.key).lineDownSeconds += seconds;
      if (c.reason) {
        const r = reasons.get(c.key) ?? new Map<string, number>();
        r.set(c.reason, (r.get(c.reason) ?? 0) + seconds);
        reasons.set(c.key, r);
      }
      if (last) current = { down: true, cause: c.key, since: downSince, reason: c.reason };
    } else if (last) {
      current = { down: false };
    }
    wasDown = down;
  }

  const causes = [...stats.values()]
    .map((s) => ({
      ...s,
      reasons: [...(reasons.get(s.key) ?? new Map<string, number>())]
        .map(([text, seconds]) => ({ text, seconds }))
        .sort((x, y) => y.seconds - x.seconds),
    }))
    .filter((s) => s.lineDownSeconds > 0 || s.waitingCausedSeconds > 0)
    .sort((x, y) => y.lineDownSeconds - x.lineDownSeconds || y.waitingCausedSeconds - x.waitingCausedSeconds);

  return { startMs, nowMs, lineDownSeconds, lineStops, causes, current };
}

// ------------------------------------------------------------------- time

/**
 * The instant of an edge shift time: the edge writes the shift's local
 * wall-clock time into UTC fields ("06:00Z" for 06:00 at the plant), the
 * transitions carry real instants. Read in the browser's zone, as the Live
 * Shift tab has always shown the shift.
 */
export function shiftInstant(iso: string): number {
  const d = new Date(iso);
  return new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds(),
  ).getTime();
}

/** An instant as local wall-clock text ("YYYY-MM-DD HH:MM:SS"), the shift-analysis timeline's format. */
export function wallClockText(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
